import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";

import { Avatar, AvatarProfile, SingleSelectInput, Text, toast } from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";

import { createFetchEmployeesForPicker } from "../../../components/Inputs/SelectEmployee";

import avatarURL from "../../../utils/avatarURL";

import { ASSIGNEE_WILDCARDS, getAssigneeWildcard } from "./todoForm";

export type AssigneeValue = {
	/** A user id or one of the wildcard references "self" | "manager" | "other". */
	id: string;
	name: string;
};

/**
 * Searchable single-select of employees with the workflow wildcard assignees (Employee,
 * Employee's Manager, Other Employee) pinned ahead of the matches — the template's
 * to-dos resolve to real people at launch time.
 */
const AssigneeSelect = ({
	value,
	onChange,
	errored,
	id,
	"data-testid": dataTestId,
}: {
	value: AssigneeValue | null;
	onChange: (value: AssigneeValue | null) => void;
	errored?: boolean;
	id?: string;
	"data-testid"?: string;
}) => {
	const queryClient = useQueryClient();

	const fetchOptions = useCallback(
		async (searchTerm: string): Promise<Option<AssigneeValue>[]> => {
			const term = searchTerm.trim().toLowerCase();
			const wildcardOptions = ASSIGNEE_WILDCARDS.filter(
				(wildcard) => !term || wildcard.name.toLowerCase().includes(term)
			).map((wildcard) => ({ label: wildcard.name, value: { id: wildcard.id, name: wildcard.name } }));

			let employeeOptions: Option<AssigneeValue>[] = [];
			try {
				const employees = await createFetchEmployeesForPicker(queryClient)(searchTerm);
				employeeOptions = employees.map((employee) => ({
					label: employee.name,
					value: { id: employee.id, name: employee.name },
				}));
			} catch (error) {
				Sentry.captureException(error);
				console.error("Error fetching employees:", error);
				toast.error("There was an error searching employees.");
			}

			return [...wildcardOptions, ...employeeOptions];
		},
		[queryClient]
	);

	const renderAssignee = useCallback((assignee: AssigneeValue) => {
		const wildcard = getAssigneeWildcard(assignee.id);
		if (wildcard) {
			return (
				<div className="grid grid-cols-[auto_1fr] items-center gap-1">
					{/* The wildcards have no user to avatar; the legacy picker's generated
					    initial images (E / EM / OE) keep them visually distinct. */}
					<Avatar size={20} url={avatarURL(wildcard.avatarInitials, 0)} name={wildcard.name} />
					<Text size="paragraph-3" weight="regular" wrap={false} dangerouslySetClassName="py-text-truncate">
						{wildcard.name}
					</Text>
				</div>
			);
		}
		return <AvatarProfile employeeId={assignee.id} employeeName={assignee.name} avatarSize={20} noLink />;
	}, []);

	return (
		<SingleSelectInput<AssigneeValue>
			fetchOptions={fetchOptions}
			value={value ?? undefined}
			onChange={onChange}
			selectionVariant="combobox"
			placeholder="Select an assignee..."
			errored={errored}
			id={id}
			data-testid={dataTestId}
			renderValue={renderAssignee}
			renderOption={(option) => renderAssignee(option.value)}
			ariaLabel="Assignee"
		/>
	);
};

export default AssigneeSelect;
